import { format, parseISO } from "date-fns";
import { Trash2 } from "lucide-react";
import { AddItemComponent } from "./AddItemComponent";

const WINDOW_OPTIONS = [
  { key: "THIS_WEEK", label: "This week" },
  { key: "THIS_AND_NEXT", label: "This + next week" },
  { key: "CUSTOM", label: "Custom" },
];

export function ShoppingListPageHeader({
  onAddItem,
  quantityUnits,
  totalItems,
  checkedItems,
  progress,
  onClearClick,
  shoppingWindow,
  onWindowChange,
  customWeeks,
  onCustomWeeksChange,
  weekStartDates,
  onOpenAiFillModal,
}) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-4 md:px-8 md:py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
          Shopping List
        </h1>

        <button
          disabled={totalItems === 0}
          onClick={onClearClick}
          className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <Trash2 className="w-4 h-4" />
          <span className="hidden sm:inline">Clear All</span>
          <span className="sm:hidden">Clear</span>
        </button>
      </div>

      {/* Shopping Window Selector */}
      <div className="mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 gap-0.5">
          {WINDOW_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onWindowChange(key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                shoppingWindow === key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {shoppingWindow === "CUSTOM" && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm text-gray-600">Shop for</span>
            <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 gap-0.5">
              {[{ label: "2 weeks", value: 3 }, { label: "3 weeks", value: 4 }].map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => onCustomWeeksChange(value)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    customWeeks === value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">
            {checkedItems} of {totalItems} items
          </span>
          <span className="text-sm font-medium text-gray-900">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Add Item */}
      <AddItemComponent
        onAddItem={onAddItem}
        quantityUnits={quantityUnits}
        onOpenAiFillModal={onOpenAiFillModal}
        addingToLabel={(() => {
          if (shoppingWindow === "THIS_WEEK" || !weekStartDates?.length) return null;
          const lastDate = weekStartDates[weekStartDates.length - 1];
          const label = shoppingWindow === "THIS_AND_NEXT" ? "Next week" : `${weekStartDates.length - 1} week${weekStartDates.length > 2 ? "s" : ""} ahead`;
          return `${label} · ${format(parseISO(lastDate), "d MMM")}`;
        })()}
      />
    </div>
  );
}
